import { presentationGenerationApi } from "@/shared/services/presentation-generation/presentation-generation.api";
import axios from "axios";
import { useRef, useState } from "react";
import type { RowRating } from "../stores/file-to-storydoc-generation.store";
import { useImportStoreApi } from "../stores/import-store-context";
import { fetchOptionSet } from "../utils/fetch-option-set";
import { trackImport } from "../utils/track-import";

const runningIdOf = (e: unknown) =>
  axios.isAxiosError(e) && e.response?.status === 409
    ? (e.response.data as { regenerationId?: string | null } | undefined)
        ?.regenerationId || null
    : null;

export const useVersionRowActions = () => {
  const store = useImportStoreApi();
  const indexOf = (pageNumber: number) =>
    store.getState().rows.findIndex((r) => r.pageNumber === pageNumber);
  const [requesting, setRequesting] = useState<ReadonlySet<number>>(
    () => new Set(),
  );
  // mirrors `requesting` synchronously so a double click can't POST twice
  const inFlight = useRef(new Set<number>());

  const markRequesting = (pageNumber: number, on: boolean) => {
    if (on) inFlight.current.add(pageNumber);
    else inFlight.current.delete(pageNumber);
    setRequesting(new Set(inFlight.current));
  };

  const handleSelect = (index: number, k: number) => {
    store.getState().selectOption(index, k);
    const row = store.getState().rows[index];
    if (!row) return;
    trackImport(
      "option changed",
      {
        pageNumber: row.pageNumber,
        k,
        title: row.options?.find((o) => o.k === k)?.title,
      },
      store,
    );
  };

  const handleStatic = (index: number, on: boolean) => {
    const row = store.getState().rows[index];
    if (!row) return;
    store.getState().setStatic(index, on);
    trackImport("static toggled", { pageNumber: row.pageNumber, on }, store);
  };

  const handleRegenerate = async (index: number) => {
    const { rows, jobId } = store.getState();
    const row = rows[index];
    if (
      !row ||
      !jobId ||
      row.regenerating ||
      row.undoing ||
      inFlight.current.has(row.pageNumber)
    )
      return;
    const { pageNumber } = row;
    trackImport("regenerate", { pageNumber }, store);
    markRequesting(pageNumber, true);
    const begin = (regenerationId: string) => {
      if (store.getState().jobId !== jobId) return;
      store.getState().startRegeneration(indexOf(pageNumber), regenerationId);
    };
    try {
      const { regenerationId } =
        await presentationGenerationApi.regenerateSlide(jobId, pageNumber);
      begin(regenerationId);
    } catch (e) {
      const running = runningIdOf(e);
      if (running) {
        begin(running);
        return;
      }
      if (store.getState().jobId !== jobId) return;
      store.getState().failRegeneration(indexOf(pageNumber), null);
      trackImport(
        "regenerate result",
        {
          pageNumber,
          ok: false,
          code: axios.isAxiosError(e)
            ? (e.response?.status ?? "network")
            : "unknown",
        },
        store,
      );
    } finally {
      markRequesting(pageNumber, false);
    }
  };

  const handleUndo = async (index: number) => {
    const { rows, jobId } = store.getState();
    const row = rows[index];
    if (
      !row?.undo ||
      !jobId ||
      row.regenerating ||
      row.undoing ||
      inFlight.current.has(row.pageNumber)
    )
      return;
    const { pageNumber } = row;
    trackImport("undo", { pageNumber }, store);
    if (row.undo.options) {
      store.getState().undo(index);
      return;
    }
    store.getState().setUndoing(index, true);
    try {
      const options = await fetchOptionSet({
        jobId,
        pageNumber,
        rowIndex: index,
        setRef: row.undo.setRef,
      });
      if (store.getState().jobId !== jobId) return;
      const i = indexOf(pageNumber);
      store.getState().undo(i, options);
      if (store.getState().rows[i]?.undoing)
        store.getState().setUndoing(i, false);
    } catch {
      if (store.getState().jobId !== jobId) return;
      store.getState().setUndoing(indexOf(pageNumber), false);
      trackImport("error", { stage: "undo", code: "fetch" }, store);
    }
  };

  const handleRate = (index: number, rating: RowRating) => {
    store.getState().setRating(index, rating);
    const row = store.getState().rows[index];
    if (!row) return;
    trackImport(
      "rating",
      {
        pageNumber: row.pageNumber,
        rating: row.rating,
        optionTitles: row.options?.map((o) => o.title) ?? [],
        selectedK: row.options?.[row.selected]?.k ?? null,
      },
      store,
    );
  };

  return {
    requesting,
    handleSelect,
    handleStatic,
    handleRegenerate,
    handleUndo,
    handleRate,
  };
};
